<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="1000px"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        icon
        v-on="on"
      >
        <v-icon color="hrissecondary">
          mdi-plus-circle
        </v-icon>
      </v-btn>
    </template>
    
    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Previous Remuneration YTD (TP3)"
      text="Create Previous Remuneration YTD"
      class="mb-0"
    >
      <v-container v-if="dialog">
        <v-row>
          <v-col
            cols="6"
            class="pb-0"
          >
            <v-menu
              v-model="previousCompanyResignedDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="resignedDateDisplay"
                  v-validate="'required'"
                  :error-messages="errors.collect('resignedDateDisplay')"
                  data-vv-name="resignedDateDisplay"  
                  data-vv-as="resign date"    
                  label="Previous Company Resigned Date"
                  outlined
                  readonly
                  class="label-position"
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon>
                      mdi-calendar
                    </v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="previousCompanyResignedDate"
                no-title
                @input="previousCompanyResignedDateMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col
            cols="6"
            class="pb-0"
          >
            <v-text-field
              v-model="accumulatedPreviousRemuneration"
              v-validate="'required'"
              v-money="money"
              data-vv-name="accumulatedPreviousRemuneration"  
              data-vv-as="previous remuneration"
              :error-messages="errors.collect('accumulatedPreviousRemuneration')"
              label="Accumulated Remuneration"
              outlined
              prefix="RM"
            />
          </v-col>

          <v-col
            cols="6"
            class="pb-0"
          >
            <v-text-field
              v-model="accumulatedPcb"
              v-validate="'required'"
              v-money="money"  
              :error-messages="errors.collect('accumulatedPcb')"
              data-vv-name="accumulatedPcb"
              data-vv-as="accumulated PCB"
              label="Accumulated PCB"
              outlined
              prefix="RM"
            />
          </v-col>

          <v-col
            cols="6"
            class="pb-0"
          >
            <v-text-field
              v-model="accumulatedEpf"
              v-validate="'required'"
              v-money="money"
              label="Accumulated EPF"
              prefix="RM"
              :error-messages="errors.collect('accumulatedEpf')"
              data-vv-name="accumulatedEpf"  
              data-vv-as="accumulated EPF"
              outlined
            />
          </v-col>

          <v-col
            cols="6"
            class="pb-0"
          >
            <v-text-field
              v-model="accumulatedSocso"
              v-validate="'required'"
              v-money="money"
              label="Accumulated SOCSO"
              prefix="RM"
              :error-messages="errors.collect('accumulatedSocso')"
              data-vv-name="accumulatedSocso"  
              outlined
              data-vv-as="accumulated SOCSO"
            />
          </v-col>

          <v-col
            cols="6"
            class="pb-0"
          >
            <v-text-field
              v-model="accumulatedEis"
              v-validate="'required'"
              v-money="money"
              label="Accumulated EIS"
              prefix="RM"
              :error-messages="errors.collect('accumulatedEis')"
              data-vv-name="accumulatedEis"  
              outlined
              data-vv-as="accumulated EIS"
            />
          </v-col>

          <v-col
            cols="6"
            class="pb-0"
          >
            <v-text-field
              v-model="accumulatedZakat"
              v-money="money"
              label="Amount of Zakat"
              prefix="RM"
              :error-messages="errors.collect('accumulatedZakat')"
              data-vv-name="accumulatedZakat"  
              outlined
              data-vv-as="amount of Zakat"
            />
          </v-col>

          <v-col
            cols="6"
            class="pb-0"
          >
            <v-text-field
              v-model="accumulatedLevyExemption"
              v-money="money"
              label="Levy Exemption for Umrah Travel"
              prefix="RM"
              data-vv-name="accumulatedLevyExemption"  
              data-vv-as="levy exemption for umrah travel"
              outlined
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col class="py-0">
            <span class="font-weight-bold pr-2">Tax Exemptions:</span>
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-icon
                  color="primary"
                  v-on="{ ...dialog, ...tooltip }"
                  @click="addTaxExemptionRow"
                >
                  mdi-plus
                </v-icon>
              </template>
              <span>Add new row</span>
            </v-tooltip>
          </v-col>
        </v-row>

        <v-row
          v-for="(selectedTaxExemption,counter) in selectedTaxExemptions"
          :key="selectedTaxExemption.id"
        >
          <v-col class="py-0">
            <v-autocomplete
              v-model="selectedTaxExemption.tax_exemption_id"
              v-validate="'required'"
              label="Tax Exemption"
              :items="taxExemptions"
              :item-text="item => '['+item.code+'] - '+ item.name"
              item-value="id"
              :error-messages="errors.collect('tax_exemption_id')"    
              data-vv-name="tax_exemption_id" 
              data-vv-as="tax exemption"
              outlined
            >
              <template v-slot:item="data">
                <v-list-item-content style="width:270px">
                  <div class="font-weight-bold">
                    [{{ data.item.code }}] - {{ data.item.name }}
                  </div>
                  <div
                    class="font-weight-bold"
                    v-html="data.item.description"
                  />
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col class="py-0">
            <v-text-field
              v-model="selectedTaxExemption.amount"
              v-money="money"
              label="Amount"
              prefix="RM"
              outlined
            />
          </v-col>

          <v-col>
            <v-icon
              color="error"
              @click="deleteTaxExemptionRow(counter)"
            >
              mdi-window-close
            </v-icon>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="py-0">
            <span class="font-weight-bold pr-2">Tax Reliefs:</span>
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-icon
                  color="primary"
                  v-on="{ ...dialog, ...tooltip }"
                  @click="addTaxReliefRow"
                >
                  mdi-plus
                </v-icon>
              </template>
              <span>Add new row</span>
            </v-tooltip>
          </v-col>
        </v-row>

        <v-row
          v-for="(selectedTaxRelief,counter) in selectedTaxReliefs"
          :key="counter"
        >
          <v-col class="py-0">
            <v-autocomplete
              v-model="selectedTaxRelief.tax_relief_id"
              v-validate="'required'"
              label="Tax Relief"
              :items="taxReliefs"
              :item-text="item => '['+item.code+'] - '+ item.name"
              item-value="id"
              :error-messages="errors.collect('tax_relief_id')"    
              data-vv-name="tax_relief_id" 
              data-vv-as="tax relief"
              outlined
            >
              <template v-slot:item="data">
                <v-list-item-content style="width:270px">
                  <div class="font-weight-bold">
                    [{{ data.item.code }}] - {{ data.item.name }}
                  </div>
                  <div
                    class="font-weight-bold"
                    v-html="data.item.description"
                  />
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col class="py-0">
            <v-text-field
              v-model="selectedTaxRelief.amount"
              v-money="money"
              label="Amount"
              prefix="RM"
              outlined
            />
          </v-col>

          <v-col>
            <v-icon
              color="error"
              @click="deleteTaxReliefRow(counter)"
            >
              mdi-window-close
            </v-icon>
          </v-col>
        </v-row>
      </v-container>

      <v-card-actions v-if="dialog">
        <v-spacer />
        <v-btn
          text
          :disabled="progressing"
          class="px-10"
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          color="hrissecondary"    
          class="white--text px-10"    
          :loading="progressing"
          @click="submitForm"
        >
          Submit
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  import { VMoney } from 'v-money'
  export default {
    directives: { money: VMoney },
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      companyEmployeeUid: {
        type: String,
        default: null,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        accumulatedEpf: null,
        accumulatedSocso: null,
        accumulatedPcb: null,
        accumulatedEis: null,
        accumulatedZakat: null,
        accumulatedLevyExemption: null,
        accumulatedPreviousRemuneration: null,
        previousCompanyResignedDateMenu: false,
        previousCompanyResignedDate: null,
        selectedTaxExemptions: [],
        taxExemptions: [],
        amount: null,
        selectedTaxReliefs: [],
        money: {
          decimal: '.',
          thousands: ',',
          precision: 2,
          masked: false, /* doesn't work with directive */
        },
        taxReliefs: [],
        yearFilter: parseInt(this.$moment().format('YYYY')),
      }
    },
    computed: {
      resignedDateDisplay () {
        return this.previousCompanyResignedDate ? this.$moment(this.previousCompanyResignedDate, 'YYYY-MM-DD').format('DD-MM-YYYY') : ''
      },
    },
    watch: {
      dialog: function (val) {
        if (!val) return

        Promise.all([
          this.$api.taxExemptions().list({ year: this.yearFilter }),
          this.$api.taxReliefs().list({ is_compulsory_deduction: 0, year: this.yearFilter }),
        ]).then(([taxExemptions, taxReliefs]) => {
          this.taxExemptions = _.orderBy(taxExemptions.data, ['name'], ['asc'])
          this.taxReliefs = _.orderBy(taxReliefs.data, ['name'], ['asc'])
        })
      },
    },

    methods: {
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return
            this.progressing = true

            this.$api.hrisPreviousRemunerations().create(this.companyEmployeeUid, this.prepareFormData())
              .then(res => {
                this.$emit('created')
                this.progressing = false
                this.dialog = false
              })
              .catch(error => {
                this.progressing = false
                if (error.response.status === 405) {
                  this.$snack({ 
                    color: 'error',
                    message: error.response.data.message, 
                  })
                }
              })
          })
      },
      prepareFormData () {
        return {
          recorded_date: this.previousCompanyResignedDate,
          accumulated_remuneration: this.accumulatedPreviousRemuneration.toString().replaceAll(',', ''),
          accumulated_epf: this.accumulatedEpf.toString().replaceAll(',', ''),
          accumulated_pcb: this.accumulatedPcb.toString().replaceAll(',', ''),
          accumulated_socso: this.accumulatedSocso.toString().replaceAll(',', ''),
          accumulated_eis: this.accumulatedEis.toString().replaceAll(',', ''),
          accumulated_zakat: this.accumulatedZakat.toString().replaceAll(',', ''),
          accumulated_levy_exemption: this.accumulatedLevyExemption.toString().replaceAll(',', ''),
          tax_exemptions: this.customTaxExemptionData(this.selectedTaxExemptions),
          tax_reliefs: this.customTaxReliefData(this.selectedTaxReliefs),
        }
      },

      addTaxExemptionRow () {
        this.selectedTaxExemptions.push({ id: this.selectedTaxExemptions.length + 1, tax_exemption_id: null, amount: null })
      },

      deleteTaxExemptionRow (counter) {
        this.selectedTaxExemptions.splice(counter, 1)
      },
      customTaxExemptionData (selectedTaxExemptions) {
        let formattedDetails = []

        selectedTaxExemptions.map(selectedTaxExemption => {
          formattedDetails.push({ tax_exemption_id: selectedTaxExemption.tax_exemption_id, amount: selectedTaxExemption.amount.toString().replaceAll(',', '') })
        })

        return formattedDetails
      },

      addTaxReliefRow () {
        this.selectedTaxReliefs.push({ id: this.selectedTaxReliefs.length + 1, tax_relief_id: null, amount: null })
      },
      deleteTaxReliefRow (counter) {
        this.selectedTaxReliefs.splice(counter, 1)
      },
      customTaxReliefData (selectedTaxReliefs) {
        let formattedDetails = []

        selectedTaxReliefs.map(selectedTaxRelief => {
          formattedDetails.push({ tax_relief_id: selectedTaxRelief.tax_relief_id, amount: selectedTaxRelief.amount.toString().replaceAll(',', '') })
        })

        return formattedDetails
      },
    },
  }
</script>

<style scoped>
.label-position >>> .v-label.v-label--active{
  margin-left:-28px;
  background:white;
  padding-right:5px
}
</style>
